import type { PromotionClient, UseQueryOverrideOptions } from '../../types'
import { useCookie, useOpenapi, useUserInfo, useCoreConfig, useQuery, useMutation, useRoute } from '#imports'

export interface PlayerOfferBody {
    id: string
}

/**
 * @title usePromotionClient
 * @see Docs https://promotion-stage.spilnu.dk/api#/
 */
export const usePromotionClient = () => {
    const { site } = useCoreConfig()
    const client = useOpenapi<PromotionClient.paths>({
        baseUrl: '/gateway/promotion/api',
        service: 'promotion'
    })

    const useGetSignupOffer = (options: UseQueryOverrideOptions = {}) => {
        const route = useRoute()
        const signupBonusCode = useCookie('signup-bonus:code')
        const { isLoggedIn } = useUserInfo()

        const { enabled = !isLoggedIn.value, lazy = true, server = false } = options

        const query = useQuery(async () => {
            const { data } = await client.GET('/public/promotions/signup-offers', {
                params: {
                    query: {
                        brand: site.brand,
                        code: signupBonusCode.value ?? route.query?.code,
                    }
                }
            })

            return data
        }, {
            key: 'promotionClient:signup-offer',
            server,
            lazy,
            enabled
        })

        return query
    }

    const useActivateOffer = useMutation(async (body: PlayerOfferBody) => {
        return client.PUT('/public/player-offers/{id}/activate', {
            params: {
                path: {
                    id: body.id
                }
            }
        })
    })

    const useAcceptOffer = useMutation(async (body: PlayerOfferBody) => {
        return client.PUT('/public/player-offers/{id}/accept', {
            params: {
                path: {
                    id: body.id
                }
            }
        })
    })

    const useRejectOffer = useMutation(async (body: PlayerOfferBody) => {
        return client.PUT('/public/player-offers/{id}/reject', {
            params: {
                path: {
                    id: body.id
                }
            }
        })
    })

    const useCancelOffer = useMutation(async (body: PlayerOfferBody) => {
        return client.PUT('/public/player-offers/{id}/cancel', {
            params: {
                path: {
                    id: body.id
                }
            }
        })
    })

    return {
        client,
        useGetSignupOffer,
        useActivateOffer,
        useAcceptOffer,
        useRejectOffer,
        useCancelOffer,
    }
}
